import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded'
import { Box, Chip, InputAdornment, Stack, Typography } from '@mui/material'
import React, { FC } from 'react'
import { FixedRoute } from '../../../../api/supplier/suppliers.types'
import { Location } from '../../../../api/models/Common/Location'
import { VehicleFixedRoute } from '../../../../api/vehicle/taxisAPI.types'
import FormInput from '../../../FormFields/FormInput/FormInput'

interface ViewFixedRoutePricingProps {
  index: number
  isEdit: boolean
  vehicleFixedRoute: VehicleFixedRoute
  chargeExceptions: any[] | undefined
  fixedRoutes: FixedRoute[]
}

const formatLocation = (location?: Location & { isPartial?: boolean }) => {
  if (!location) return ''
  if (location.isPartial) return `${location.postCode} (Partial)`
  if (location.formattedAddress) return location.formattedAddress
  return [location.addressLine1, location.town, location.postCode]
    .filter(Boolean)
    .join(', ')
}

const formatPrice = (value: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value)

const ViewFixedRoutePricing: FC<ViewFixedRoutePricingProps> = (props) => {
  const { vehicleFixedRoute, chargeExceptions, fixedRoutes, index, isEdit } =
    props

  const route = fixedRoutes?.find(
    (fixedRoute) => fixedRoute.id === vehicleFixedRoute.fixedRouteId,
  )

  const getChargeExceptionName = (id: number) =>
    chargeExceptions?.find((c) => c.id === id)?.name ?? 'Exception'

  const formatExceptionValue = (chargeTypeName: string, value: number) => {
    if (chargeTypeName === 'Fixed') return formatPrice(value)
    if (chargeTypeName === 'Percentage') return `${value}%`
    return `${value}`
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {route?.name ?? 'Fixed route'}
          </Typography>
          {(route?.pickup || route?.dropOff) && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 0.5, color: 'text.secondary' }}
            >
              <Typography variant="body2" noWrap>
                {formatLocation(route?.pickup)}
              </Typography>
              <ArrowForwardRounded fontSize="small" />
              <Typography variant="body2" noWrap>
                {formatLocation(route?.dropOff)}
              </Typography>
            </Stack>
          )}
        </Box>
        {isEdit ? (
          <Box sx={{ width: 140 }}>
            <FormInput
              name={`fixedRoutes.${index}.price`}
              label="Price"
              type="number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
                inputProps: { step: '0.01', min: 0 },
              }}
            />
          </Box>
        ) : (
          <Chip
            label={formatPrice(vehicleFixedRoute.price)}
            color="primary"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Stack>

      {vehicleFixedRoute?.chargeExceptions?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            Charge exceptions
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 1 }}
          >
            {vehicleFixedRoute.chargeExceptions.map((exception, i) => (
              <Chip
                key={i}
                size="small"
                variant="outlined"
                label={`${getChargeExceptionName(
                  exception.chargeExceptionId,
                )} · ${formatExceptionValue(
                  exception.chargeTypeName,
                  exception.value,
                )}`}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export default ViewFixedRoutePricing
